import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Receipt,
  Plus,
  Pencil,
  Check,
  X,
  Loader2,
  Trash2,
  CircleCheck,
  TriangleAlert,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  formatUsd,
  invoiceTotals,
  invoiceReconciliation,
  deriveInvoiceStatus,
  addInvoiceLine,
  updateInvoiceLine,
  deleteInvoiceLine,
  markInvoicePaid,
} from '@/lib/jobsData';
import { toast } from 'sonner';

const STATUS_BADGE = {
  paid:
    'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30',
  overdue:
    'bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30',
  due:
    'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30',
  upcoming:
    'bg-[hsl(var(--muted))] text-foreground border-border',
};

const InvoiceRow = ({ inv, job, onUpdated, onMarkPaid, onDelete }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [desc, setDesc] = useState(inv?.desc || '');
  const [due, setDue] = useState(inv?.due || '');
  const [amount, setAmount] = useState(inv?.amount ?? 0);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const status = deriveInvoiceStatus(inv);
  const isPaid = status === 'paid';
  const STATUS_LABEL = {
    paid: t('invoices.status.paid'),
    overdue: t('invoices.status.overdue'),
    due: t('invoices.status.due'),
    upcoming: t('invoices.status.upcoming'),
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateInvoiceLine(job, inv.id, {
        desc,
        due,
        amount: Number(amount) || 0,
      });
      toast.success(t('jobs.invoice_tab.updated_toast'));
      onUpdated?.(updated);
      setEditing(false);
    } catch (e) {
      toast.error(e?.message || t('jobs.invoice_tab.update_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handlePaid = async () => {
    setBusy(true);
    try {
      const updated = await markInvoicePaid(job, inv.id);
      toast.success(
        t('jobs.invoice_tab.payment_received', {
          amount: formatUsd(inv.amount),
          desc: inv.desc || '',
        })
      );
      onMarkPaid?.(updated, inv);
    } catch (e) {
      toast.error(e?.message || t('jobs.invoice_tab.mark_paid_failed'));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      const updated = await deleteInvoiceLine(job, inv.id);
      toast.success(t('jobs.invoice_tab.line_removed'));
      onDelete?.(updated);
      setConfirmDelete(false);
    } catch (e) {
      toast.error(e?.message || t('jobs.invoice_tab.line_remove_failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-md border border-border bg-[hsl(var(--background))]/40 px-3.5 py-3"
      data-testid={`invoice-${inv.id}`}
    >
      {editing ? (
        <div className="space-y-2" data-testid={`invoice-edit-${inv.id}`}>
          <Input
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder={t('jobs.invoice_tab.desc_placeholder')}
            className="h-9 bg-[hsl(var(--muted))] border-border text-foreground"
            data-testid={`invoice-edit-desc-${inv.id}`}
          />
          <div className="grid grid-cols-2 gap-2">
            <Input
              value={due}
              onChange={(e) => setDue(e.target.value)}
              placeholder={t('jobs.invoice_tab.due_placeholder')}
              className="h-9 bg-[hsl(var(--muted))] border-border text-foreground"
              data-testid={`invoice-edit-due-${inv.id}`}
            />
            <Input
              type="number"
              inputMode="decimal"
              step="100"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={t('jobs.invoice_tab.amount_placeholder')}
              className="h-9 bg-[hsl(var(--muted))] border-border text-foreground"
              data-testid={`invoice-edit-amount-${inv.id}`}
            />
          </div>
          <div className="flex items-center justify-end gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setEditing(false)}
              disabled={saving}
              className="text-muted-foreground hover:text-foreground"
            >
              <X size={14} />
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
              data-testid={`invoice-save-${inv.id}`}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check size={14} />
              )}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="min-w-0 text-sm font-semibold text-foreground break-words">
                  {inv?.desc || t('jobs.invoice_tab.invoice_line_default')}
                </span>
                <Badge
                  variant="outline"
                  className={cn(
                    'uppercase text-[10px] tracking-[0.16em]',
                    STATUS_BADGE[status] || STATUS_BADGE.upcoming
                  )}
                  data-testid={`invoice-status-${inv.id}`}
                >
                  {STATUS_LABEL[status] || STATUS_LABEL.upcoming}
                </Badge>
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {t('jobs.invoice_tab.due_label')} {inv?.due || t('common.tbd')} · {inv?.id}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-sm font-semibold text-foreground">
                {formatUsd(inv?.amount)}
              </div>
              {isPaid ? (
                <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--success))]">
                  <CircleCheck size={12} />
                  {t('jobs.invoice_tab.receipt_on_file')}
                </div>
              ) : null}
            </div>
          </div>
          <div className="mt-3 flex items-center justify-end gap-1">
            {!isPaid ? (
              <Button
                size="sm"
                onClick={handlePaid}
                disabled={busy}
                className="bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/25 border border-[hsl(var(--success))]/30 uppercase tracking-[0.16em] text-xs"
                data-testid={`invoice-mark-paid-${inv.id}`}
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Check size={14} className="mr-1.5" />
                    {t('jobs.invoice_tab.mark_paid')}
                  </>
                )}
              </Button>
            ) : null}
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setEditing(true)}
              className="text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))]"
              data-testid={`invoice-edit-button-${inv.id}`}
              aria-label={t('jobs.invoice_tab.edit_aria')}
            >
              <Pencil size={14} />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setConfirmDelete(true)}
              className="text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10"
              data-testid={`invoice-delete-${inv.id}`}
              aria-label={t('jobs.invoice_tab.delete_aria')}
            >
              <Trash2 size={14} />
            </Button>
          </div>
        </>
      )}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent
          className="bg-[hsl(var(--card))] border-border"
          data-testid={`invoice-delete-confirm-${inv.id}`}
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">
              {t('jobs.invoice_tab.delete_title')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              {t('jobs.invoice_tab.delete_description', { name: inv?.desc || '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-border text-foreground hover:bg-[hsl(var(--muted))]"
              disabled={busy}
            >
              {t('actions.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={busy}
              className="bg-[hsl(var(--destructive))] text-destructive-foreground hover:bg-[hsl(var(--destructive))]/90"
              data-testid={`invoice-delete-confirm-action-${inv.id}`}
            >
              {t('jobs.invoice_tab.remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const JobInvoicesTab = ({ job, onUpdated }) => {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const totals = invoiceTotals(job?.invoices || []);
  const reconcile = invoiceReconciliation(job);

  const handleAdd = async () => {
    setAdding(true);
    try {
      const updated = await addInvoiceLine(job, {
        desc: t('jobs.invoice_tab.new_line_default'),
        amount: 0,
        due: t('common.tbd'),
      });
      toast.success(t('jobs.invoice_tab.new_line_added'));
      onUpdated?.(updated);
    } catch (e) {
      toast.error(e?.message || t('jobs.invoice_tab.new_line_failed'));
    } finally {
      setAdding(false);
    }
  };

  const invoices = Array.isArray(job?.invoices) ? job.invoices : [];

  return (
    <div className="space-y-4" data-testid="job-invoices-tab">
      {/* Invoice/contract reconciliation warning */}
      {reconcile.warn ? (
        <div
          className="flex items-start gap-2.5 rounded-md border bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30 p-3"
          data-testid="invoices-reconcile-warning"
        >
          <TriangleAlert size={16} className="mt-0.5 shrink-0" />
          <div className="text-xs">
            <div className="font-semibold uppercase tracking-[0.14em] text-[11px]">
              {reconcile.gap > 0
                ? t('jobs.invoice_tab.reconcile_under_title')
                : t('jobs.invoice_tab.reconcile_over_title')}
            </div>
            <div className="mt-1 text-foreground/80">
              {reconcile.gap > 0
                ? t('jobs.invoice_tab.reconcile_under_body', {
                    contract: formatUsd(reconcile.contractValue),
                    invoiced: formatUsd(reconcile.invoicedTotal),
                    gap: formatUsd(reconcile.gap),
                  })
                : t('jobs.invoice_tab.reconcile_over_body', {
                    contract: formatUsd(reconcile.contractValue),
                    invoiced: formatUsd(reconcile.invoicedTotal),
                    gap: formatUsd(Math.abs(reconcile.gap)),
                  })}
            </div>
          </div>
        </div>
      ) : null}

      {/* Totals row */}
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-4 grid grid-cols-3 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {t('jobs.invoice_tab.total')}
            </div>
            <div className="mt-1 text-base font-semibold text-foreground">
              {formatUsd(totals.total)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {t('jobs.invoice_tab.paid')}
            </div>
            <div className="mt-1 text-base font-semibold text-[hsl(var(--success))]">
              {formatUsd(totals.paid)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {t('jobs.invoice_tab.outstanding')}
            </div>
            <div className="mt-1 text-base font-semibold text-foreground">
              {formatUsd(totals.outstanding)}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Receipt size={14} className="text-[hsl(var(--primary))]" />
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                {t('jobs.invoice_tab.list_label', { count: invoices.length })}
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleAdd}
              disabled={adding}
              className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
              data-testid="invoice-add-button"
            >
              {adding ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus size={14} className="mr-1.5" />
              )}
              {t('jobs.invoice_tab.add_line')}
            </Button>
          </div>
          {invoices.length === 0 ? (
            <div className="mt-4 text-sm text-muted-foreground">
              {t('jobs.invoice_tab.empty')}
            </div>
          ) : (
            <div className="mt-3 space-y-2" data-testid="invoices-list">
              {invoices.map((inv) => (
                <InvoiceRow
                  key={inv.id}
                  inv={inv}
                  job={job}
                  onUpdated={onUpdated}
                  onMarkPaid={onUpdated}
                  onDelete={onUpdated}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default JobInvoicesTab;
